import React, { useMemo } from "react";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { createTheme } from "@mui/material/styles";
import { arEG, heIL as coreHeIL, enUS as coreEnUS } from "@mui/material/locale";
import { arSD, heIL as gridHeIL, enUS as gridEnUS } from "@mui/x-data-grid/locales";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { prefixer } from "stylis";
import rtlPlugin from "stylis-plugin-rtl";
import { useLanguage } from "../../context/LanguageContext";

// Emotion cache that mirrors every generated style (margins, paddings, left/right,
// borders...) so MUI components lay out right-to-left for Arabic and Hebrew.
const rtlCache = createCache({ key: "muirtl", stylisPlugins: [prefixer, rtlPlugin] });

const LOCALES = {
  en: [coreEnUS, gridEnUS],
  ar: [arEG, arSD],
  he: [coreHeIL, gridHeIL],
};

// Arabic glyphs render in Almarai like the rest of the site (see index.css);
// Sanchez has no Arabic glyphs.
const LANGUAGE_FONTS = {
  ar: '"Almarai"',
};

// createTheme has already resolved fontFamily into each variant, so a font
// override must be applied to every variant, not just typography.fontFamily.
const TYPOGRAPHY_VARIANTS = [
  "h1", "h2", "h3", "h4", "h5", "h6",
  "subtitle1", "subtitle2", "body1", "body2", "button", "caption", "overline",
];

function fontOverride(fontFamily) {
  return {
    fontFamily,
    ...Object.fromEntries(TYPOGRAPHY_VARIANTS.map((variant) => [variant, { fontFamily }])),
  };
}

export default function DirectionalThemeProvider({ theme, children }) {
  const { language, isRTL } = useLanguage();
  const direction = isRTL ? "rtl" : "ltr";

  const localizedTheme = useMemo(() => {
    const languageFont = LANGUAGE_FONTS[language];
    return createTheme(
      theme,
      {
        direction,
        ...(languageFont && {
          typography: fontOverride([languageFont, theme.typography.fontFamily].join(",")),
        }),
      },
      ...(LOCALES[language] || LOCALES.en)
    );
  }, [theme, language, direction]);

  const content = (
    <ThemeProvider theme={localizedTheme}>
      <CssBaseline />
      <div dir={direction}>{children}</div>
    </ThemeProvider>
  );

  return isRTL ? <CacheProvider value={rtlCache}>{content}</CacheProvider> : content;
}
